import { SectionShell } from "@/components/design-system/SectionShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckboxOption } from "@/components/ui/checkbox";
import { RadioGroup, RadioOption } from "@/components/ui/radio";
import { Select } from "@/components/ui/select";

const code = `import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckboxOption } from "@/components/ui/checkbox";
import { RadioGroup, RadioOption } from "@/components/ui/radio";
import { Select } from "@/components/ui/select";

<form className="space-y-4">
  {/* Text input */}
  <div className="space-y-2">
    <label htmlFor="email">Email</label>
    <Input id="email" type="email" placeholder="you@example.com" />
    <p className="text-xs text-ink-muted">We'll only use this for account notifications.</p>
  </div>

  {/* Select */}
  <div className="space-y-2">
    <label htmlFor="country">Country</label>
    <Select id="country" defaultValue="">
      <option value="" disabled>Choose one…</option>
      <option value="us">United States</option>
      <option value="ca">Canada</option>
      <option value="uk">United Kingdom</option>
    </Select>
  </div>

  {/* Radio group */}
  <fieldset className="space-y-2">
    <legend>Plan</legend>
    <RadioGroup>
      <RadioOption name="plan" value="free" defaultChecked label="Free" />
      <RadioOption name="plan" value="pro" label="Pro" description="For growing teams." />
      {/* Trailing controls go in children — beside the label, not inside it */}
      <RadioOption name="plan" value="team" label="Team of">
        <Select aria-label="Team size" wrapperClassName="w-24" defaultValue="5">
          <option value="5">5</option>
          <option value="10">10</option>
        </Select>
      </RadioOption>
    </RadioGroup>
  </fieldset>

  {/* Checkbox */}
  <CheckboxOption
    defaultChecked
    label="Send me product updates"
    description="Roughly one email per month."
  />

  <Button type="submit">Save</Button>
</form>`;

export function FormsSection() {
  return (
    <SectionShell
      id="forms"
      title="Forms"
      description={
        <>
          Forms compose <code>&lt;Input&gt;</code>, <code>&lt;Select&gt;</code>,
          <code>&lt;Checkbox&gt;</code>, <code>&lt;Radio&gt;</code>, native HTML
          labels, helper text, and <code>&lt;Button&gt;</code>. Vertical spacing between
          fields uses <code>space-y-4</code>; spacing inside a field uses{" "}
          <code>space-y-2</code>.
        </>
      }
      whenToUse={
        <ul>
          <li>All data-entry surfaces.</li>
          <li>Wrap fields with their own <code>&lt;label&gt;</code> for accessibility.</li>
          <li>Use <code>&lt;fieldset&gt;</code> + <code>&lt;legend&gt;</code> around radio groups.</li>
        </ul>
      }
      whenNotToUse={
        <ul>
          <li>Inline filters in toolbars — use compact controls instead.</li>
          <li>Single-button calls to action — those don't need a form wrapper unless they POST.</li>
        </ul>
      }
      preview={
        <form
          className="max-w-md space-y-4"
          onSubmit={(e) => e.preventDefault()}
        >
          <div className="space-y-2">
            <label htmlFor="ds-form-email">Email</label>
            <Input
              id="ds-form-email"
              type="email"
              placeholder="you@example.com"
            />
            <p className="text-xs text-ink-muted">
              We'll only use this for account notifications.
            </p>
          </div>

          <div className="space-y-2">
            <label htmlFor="ds-form-country">Country</label>
            <Select id="ds-form-country" defaultValue="">
              <option value="" disabled>
                Choose one…
              </option>
              <option value="us">United States</option>
              <option value="ca">Canada</option>
              <option value="uk">United Kingdom</option>
            </Select>
          </div>

          <fieldset className="space-y-2">
            <legend>Plan</legend>
            <RadioGroup>
              <RadioOption
                name="ds-form-plan"
                value="free"
                defaultChecked
                label="Free"
              />
              <RadioOption
                name="ds-form-plan"
                value="pro"
                label="Pro"
                description="For growing teams."
              />
              <RadioOption name="ds-form-plan" value="team" label="Team of">
                <Select
                  aria-label="Team size"
                  wrapperClassName="w-24"
                  defaultValue="5"
                >
                  <option value="5">5</option>
                  <option value="10">10</option>
                </Select>
              </RadioOption>
            </RadioGroup>
          </fieldset>

          <CheckboxOption
            id="ds-form-newsletter"
            defaultChecked
            label="Send me product updates"
            description="Roughly one email per month."
          />

          <Button type="submit">Save</Button>
        </form>
      }
      code={code}
      options={
        <ul className="list-disc pl-5">
          <li>Use HTML <code>&lt;label htmlFor&gt;</code> with the field's <code>id</code> for accessibility.</li>
          <li>Helper text uses <code>text-xs text-ink-muted</code> directly under the field.</li>
          <li>Error states: render a <code>text-xs text-danger-display</code> message in the same slot as helper text and add <code>aria-invalid</code> to the field.</li>
          <li>Radio groups: wrap in <code>&lt;fieldset&gt;</code> + <code>&lt;legend&gt;</code> and share a <code>name</code> across all <code>&lt;RadioOption&gt;</code>s.</li>
          <li>Options with words: use <code>&lt;RadioOption&gt;</code> and <code>&lt;CheckboxOption&gt;</code>, never a bare <code>&lt;Radio&gt;</code> or <code>&lt;Checkbox&gt;</code> inside your own <code>flex</code> <code>&lt;label&gt;</code>. A flex label stretches the full width of its column, so a click in the blank space far to the right changes the value. The options make the box and its words clickable and nothing beyond them.</li>
          <li>Controls that belong to an option (a <code>&lt;Select&gt;</code>, a trailing phrase) go in its <code>children</code>, which render beside the label rather than inside it. Their <code>onChange</code> should select the option explicitly, since clicking them no longer does.</li>
          <li>Selects: use a disabled empty <code>&lt;option&gt;</code> as a placeholder when no default makes sense.</li>
          <li>Narrowing a select: put the width on <code>wrapperClassName</code>, not <code>className</code>. The chevron is positioned against the wrapper, so a width on the <code>&lt;select&gt;</code> alone strands it at the right edge of a full-width box.</li>
        </ul>
      }
    />
  );
}
